import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { toast as sonnerToast } from "sonner";
import { tr } from "./i18n.js";

/**
 * Notification Center — persists every toast surfaced by `sonner`
 * into a bounded log so editors can review what fired after the toast
 * itself auto-dismissed. Design intent:
 *   - Toasts still auto-close in 4s (see App.js).
 *   - Nothing goes fully "gone" — the log holds the last 100 items.
 *   - Log survives reload (localStorage) so an editor can catch up
 *     on the previous session too.
 *
 * ---------------------------------------------------------------------
 * Why we monkey-patch `toast.*` instead of using `useSonner()`
 * ---------------------------------------------------------------------
 * The previous implementation subscribed to sonner via `useSonner()`
 * and mirrored the currently-visible toast list into our log. That
 * missed notifications intermittently in production because
 * `useSonner` in sonner v2 uses an internal `setTimeout(0)` +
 * `ReactDOM.flushSync` bridge to work around React 18 batching, and:
 *
 *   1. Bursts of toasts (e.g. batch article writes firing many
 *      `toast.success` calls in <100ms) collapse into a single React
 *      commit where only the FINAL toast is present in `activeToasts`
 *      — the intermediate ones are added and removed inside the same
 *      timeout window and our `useEffect` never sees them.
 *   2. If the user opens the Notifications dialog RIGHT WHILE a toast
 *      is arriving, the parent modal repaint can starve the sonner
 *      setTimeout callback long enough that the effect sees the
 *      "empty" state and skips the toast entirely.
 *
 * Symptom that made this obvious: the user reported the Notifications
 * box holding only entries "22h ago" — i.e. the previous session was
 * captured fine (via reload/persisted log), but the current session's
 * bursts of toasts silently dropped.
 *
 * Fix: intercept EVERY toast at the source. We wrap each method on
 * sonner's exported `toast` object exactly once at module load and
 * publish through a small in-process bus. `NotificationProvider`
 * subscribes to the bus in a useEffect. This is 100% synchronous with
 * the caller so no burst or batching can ever swallow a toast.
 *
 * The wrapping is idempotent (guarded by `__ggnPatched`) so
 * hot-reload / StrictMode double-mount don't double-wrap and
 * duplicate log entries.
 */

const STORAGE_KEY = "ggn.notifications";
const READ_KEY = "ggn.notifications.lastRead";
const MAX_ITEMS = 100;

// The sonner methods we care about. `custom` and `loading` are wrapped
// too even though we can't extract a friendly string for `custom` —
// they still show up as "(rich notification)" so an editor knows an
// event happened.
const WRAPPED_METHODS = [
  "success",
  "error",
  "warning",
  "info",
  "message",
  "loading",
  "custom",
];

// Tiny module-level pubsub. Handlers are added by NotificationProvider
// on mount and removed on unmount. Multiple providers (shouldn't
// happen, but StrictMode remounts count) will each receive events.
const _subscribers = new Set();
function _publish(entry) {
  for (const fn of _subscribers) {
    try {
      fn(entry);
    } catch (e) {
      // A single broken subscriber shouldn't take down the toast call.
      console.warn(tr("notification subscriber failed"), e);
    }
  }
}

// Best-effort human-readable text from whatever the caller passed to
// `toast.success(...)`. Sonner accepts a string OR an object with
// `title` / `description` / `message`.
function _extractMessage(arg) {
  if (arg == null) return "";
  if (typeof arg === "string") return arg;
  if (typeof arg === "object") {
    if (typeof arg.message === "string") return arg.message;
    if (typeof arg.title === "string") return arg.title;
    if (typeof arg.description === "string") return arg.description;
  }
  // React nodes / functions — mark it but don't stringify (JSX would
  // render as "[object Object]").
  return tr("(rich notification)");
}

// Patch each method on `sonnerToast` once. Guard prevents double-wrap
// under hot module reload or React StrictMode remounts.
if (!sonnerToast.__ggnPatched) {
  for (const method of WRAPPED_METHODS) {
    const original = sonnerToast[method];
    if (typeof original !== "function") continue;
    sonnerToast[method] = function patched(...args) {
      const result = original.apply(sonnerToast, args);
      // Publish AFTER sonner has decided the toast id so we can dedupe
      // against it if we ever want to (currently ids are opaque to us).
      _publish({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        ts: Date.now(),
        type: method === "message" ? "default" : method,
        message: _extractMessage(args[0]),
      });
      return result;
    };
  }
  sonnerToast.__ggnPatched = true;
}

function loadInitial() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}
function loadLastRead() {
  try {
    return Number(window.localStorage.getItem(READ_KEY)) || 0;
  } catch {
    return 0;
  }
}

const NotificationContext = createContext({
  items: [],
  unread: 0,
  markAllRead: () => {},
  clear: () => {},
});

export function NotificationProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  const [lastRead, setLastRead] = useState(loadLastRead);
  // Refs keep the latest list available inside the pubsub handler
  // without re-subscribing on every state change (which would race
  // against fast toast bursts).
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const persistItems = useCallback((next) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage blocked (private mode etc.) — best-effort.
    }
  }, []);

  // Subscribe to the toast bus once on mount. All subsequent toast
  // calls anywhere in the app land here synchronously.
  useEffect(() => {
    const handler = (entry) => {
      setItems((prev) => {
        const next = [entry, ...prev].slice(0, MAX_ITEMS);
        persistItems(next);
        return next;
      });
    };
    _subscribers.add(handler);
    return () => {
      _subscribers.delete(handler);
    };
  }, [persistItems]);

  const markAllRead = useCallback(() => {
    const now = Date.now();
    setLastRead(now);
    try {
      window.localStorage.setItem(READ_KEY, String(now));
    } catch {
      /* noop */
    }
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const unread = items.filter((i) => i.ts > lastRead).length;

  return (
    <NotificationContext.Provider
      value={{ items, unread, markAllRead, clear }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
